/** @format */

import { Chain, Environment } from '@shared/data-access/interfaces';
import { Injectable, inject } from '@angular/core';

import { CdnNames } from '../enums/cdn-names.enum';
import { ImageLoader } from '../interfaces/image-loader.interface';
import { ImageLoaderConfig } from '@angular/common';
import { SwatchColorSanitizerService } from './swatch-color-sanitizer.service';

@Injectable({
  providedIn: 'root',
})
export class Scene7ImageLoaderService implements ImageLoader {
  private readonly environment = inject(Environment);
  private readonly chain = inject(Chain);
  private readonly swatchColorSanitizerService = inject(SwatchColorSanitizerService);

  buildUrlByImageLoader(config: ImageLoaderConfig): string {
    const baseUrl = config.loaderParams?.isUrlEmbeded
      ? ''
      : this.getCdnUrlByFlag(config.loaderParams?.cdn);
    let imageName = '';

    if (config.src) {
      const mediaSetSuffix = config.loaderParams?.mediaSetSuffix || '';
      const swatchColor =
        this.swatchColorSanitizerService.sanitize(config.loaderParams?.swatchColor) || '';
      const swatchColorPrefix = config.loaderParams?.swatchColorPrefix || (swatchColor ? '_' : '');
      imageName = `${config.src}${swatchColorPrefix}${swatchColor}${mediaSetSuffix}`;
    } else {
      imageName = 'ImageUnavailablePDPSwatch';
    }

    const url = `${baseUrl}${imageName}?`;

    // Create an array to store query parameters
    const queryParams = [];

    // Add width parameter with default value 16 to the queryParams array
    queryParams.push(`wid=${config.width || 16}`);

    // Add qlt parameter with default value 70 to the queryParams array
    queryParams.push(`qlt=${config.loaderParams?.qlt || 70}`);

    // Checks if the image should have 1:1 aspect ratio and add hei equal
    // to wid to the queryParams array
    if (config.loaderParams?.squareAspectRatio) {
      queryParams.push(`hei=${config.width || 16}`);
    }
    // Check and add hei parameter to the queryParams array
    else if (config.loaderParams?.height) {
      queryParams.push(`hei=${config.loaderParams.height}`);
    }

    // Check and add resMode parameter to the queryParams array
    if (config.loaderParams?.resMode) {
      queryParams.push(`resMode=${config.loaderParams.resMode}`);
    }

    // Check and add fit parameter tothe queryParams array
    if (config.loaderParams?.fit) {
      queryParams.push(`fit=${config.loaderParams.fit}`);
    }

    // Add fmt parameter with default value 'webp' to the queryParams array
    queryParams.push(`fmt=${config.loaderParams?.fmt || 'webp'}`);

    // Add op_sharpen parameter with default value 1 to the queryParams array
    queryParams.push(`op_sharpen=${config.loaderParams?.op_sharpen || 1}`);

    return url + queryParams.join('&');
  }

  /**
   * @description Selects specific CDN URL from the environment file
   *              based on the CDN flag
   * @param cdnFlag CDN numerable flag
   * @returns CDN URL from the environment
   */
  private getCdnUrlByFlag(cdnFlag: number): string {
    const env = this.environment;
    const scene7Config = env.thirdParty.scene7?.baseConfig;
    let imageCdn: string;
    const propertiesPresent = Boolean(
      scene7Config?.baseUrl && scene7Config?.imageDirectory && scene7Config?.cdn,
    );

    switch (cdnFlag) {
      case CdnNames.DksCdn:
        return propertiesPresent
          ? `${scene7Config?.baseUrl}${scene7Config?.imageDirectory}${scene7Config?.cdn}`
          : '';
      case CdnNames.GolfGalaxyTest:
        imageCdn = scene7Config?.cdn ?? '';

        // Additional logic for 'GolfGalaxyTest' environment
        if (window.location.hostname.includes(`prod-${this.chain.seoIdentifier}`)) {
          imageCdn = 'GolfGalaxy/';
        }

        return `${scene7Config?.baseUrl}${scene7Config?.imageDirectory}${imageCdn}`;
      case CdnNames.DksFed:
        return `https:${env.api.homr.scene7Url}`;
      case CdnNames.GolfGalaxy:
        return env.thirdParty.scene7?.url ?? '';
      default:
        return env.thirdParty.scene7?.url ?? '';
    }
  }
}
